import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { communityService } from '../../services/api';
import { MAX_SEDES, MAX_SEDE_LENGTH, validateSedes } from '../../utils/schedule';

/**
 * Editor de sedes (admin) para una comunidad. Chips inline + input para
 * agregar uno nuevo. Valida tope (8 sedes, 32 chars) lado cliente; el
 * server hace la validación dura. Toggle "Permitir horarios" arriba.
 *
 * Side-effect documentado: borrar una sede no limpia los rangos de los
 * miembros que la tenían referenciada — el ScheduleDisplay los marca como
 * "(eliminada)".
 *
 * Props:
 *   communityId
 *   currentSedes: string[]
 *   scheduleEnabled: bool
 *   onToast(type, msg): handler para feedback
 */
function SedesEditor({ communityId, currentSedes = [], scheduleEnabled = false, onToast }) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  // Estado local mientras edita; se commitea al server al confirmar.
  const [sedes, setSedes] = useState(currentSedes);
  const [pending, setPending] = useState('');
  const [enabled, setEnabled] = useState(scheduleEnabled);
  const [dirty, setDirty] = useState(false);

  // Re-sync cuando llegan datos frescos del server (ej. otra tab edita).
  useEffect(() => { setSedes(currentSedes); setDirty(false); }, [currentSedes]);
  useEffect(() => { setEnabled(scheduleEnabled); }, [scheduleEnabled]);

  const saveSedesMut = useMutation({
    mutationFn: (next) => communityService.updateSedes(communityId, next),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['community', String(communityId)] });
      qc.invalidateQueries({ queryKey: ['my-schedule-context', String(communityId)] });
      setDirty(false);
      onToast?.('success', t('Sedes guardadas'));
    },
    onError: (err) => onToast?.('error', err?.response?.data?.message || t('Error al guardar las sedes')),
  });

  const toggleMut = useMutation({
    mutationFn: (value) => communityService.updateScheduleEnabled(communityId, value),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['community', String(communityId)] });
      onToast?.('success', t('Configuración guardada'));
    },
    onError: (err) => onToast?.('error', err?.response?.data?.message || t('Error al actualizar la configuración')),
  });

  const handleAdd = () => {
    const v = pending.trim();
    if (!v) return;
    if (sedes.length >= MAX_SEDES) {
      onToast?.('error', t('Máximo {{n}} sedes', { n: MAX_SEDES }));
      return;
    }
    if (v.length > MAX_SEDE_LENGTH) {
      onToast?.('error', t('Cada sede no puede tener más de {{n}} caracteres', { n: MAX_SEDE_LENGTH }));
      return;
    }
    if (sedes.some((s) => s.toLowerCase() === v.toLowerCase())) {
      onToast?.('error', t('Esa sede ya está en la lista'));
      return;
    }
    setSedes([...sedes, v]);
    setPending('');
    setDirty(true);
  };

  const handleRemove = (idx) => {
    const next = sedes.slice();
    next.splice(idx, 1);
    setSedes(next);
    setDirty(true);
  };

  const handleSave = () => {
    const { sedes: clean } = validateSedes(sedes);
    saveSedesMut.mutate(clean);
  };

  const handleToggle = () => {
    const next = !enabled;
    setEnabled(next);
    toggleMut.mutate(next);
  };

  return (
    <div className="space-y-5">
      {/* Toggle "Permitir horarios" */}
      <div className="flex items-center justify-between gap-4 p-4 rounded-xl bg-surface-container-high/30">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-on-surface">{t('Permitir horarios en esta comunidad')}</p>
          <p className="text-xs text-on-surface-variant mt-0.5">
            {t('Cuando está activado, los miembros pueden declarar sus horarios semanales por sede.')}
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          onClick={handleToggle}
          disabled={toggleMut.isPending}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
            enabled ? 'bg-primary' : 'bg-outline-variant/40'
          }`}
        >
          <span className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
            enabled ? 'translate-x-6' : 'translate-x-1'
          }`} />
        </button>
      </div>

      {/* Lista de sedes */}
      <div className="p-4 rounded-xl bg-surface-container-high/30 space-y-3">
        <div>
          <p className="text-sm font-semibold text-on-surface">{t('Sedes (puntos de encuentro)')}</p>
          <p className="text-xs text-on-surface-variant mt-0.5">
            {t('Lugares físicos donde los miembros pueden encontrarse. Máximo {{n}} sedes.', { n: MAX_SEDES })}
          </p>
        </div>

        {/* Chips */}
        <div className="flex flex-wrap gap-2 min-h-[2rem]">
          {sedes.length === 0 && (
            <p className="text-xs text-on-surface-variant/60 italic py-1">
              {t('Todavía no agregaste ninguna sede.')}
            </p>
          )}
          {sedes.map((s, idx) => (
            <span
              key={`${s}-${idx}`}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-on-surface text-xs"
            >
              <span className="material-symbols-outlined text-[14px] text-primary">place</span>
              <span>{s}</span>
              <button
                type="button"
                onClick={() => handleRemove(idx)}
                className="text-on-surface-variant hover:text-error transition-colors -mr-1"
                aria-label={t('Eliminar sede')}
              >
                <span className="material-symbols-outlined text-[14px]">close</span>
              </button>
            </span>
          ))}
        </div>

        {/* Input */}
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={pending}
            onChange={(e) => setPending(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
            maxLength={MAX_SEDE_LENGTH}
            placeholder={t('Ej: Centro · Plaza Italia · Norte')}
            disabled={sedes.length >= MAX_SEDES}
            className="flex-1 px-3 py-2 rounded-lg border border-outline-variant/30 bg-transparent text-on-surface text-sm placeholder:text-on-surface-variant/50 focus:outline-none focus:border-primary disabled:opacity-50"
          />
          <button
            type="button"
            onClick={handleAdd}
            disabled={!pending.trim() || sedes.length >= MAX_SEDES}
            className="px-3 py-2 rounded-lg bg-primary text-on-primary text-xs font-bold hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {t('Agregar')}
          </button>
        </div>

        {dirty && (
          <div className="pt-2 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => { setSedes(currentSedes); setDirty(false); }}
              className="text-xs text-on-surface-variant hover:text-on-surface font-medium px-3 py-1.5 rounded-lg hover:bg-surface-container-high/50 transition-colors"
            >
              {t('Cancelar')}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saveSedesMut.isPending}
              className="text-xs text-on-primary bg-primary font-bold px-4 py-1.5 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {saveSedesMut.isPending ? t('Guardando...') : t('Guardar sedes')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default SedesEditor;
